import React, { useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { askAiTutor } from '../../services/api';

const MAX_HISTORY = 20;

const compactMarkdown = {
  p: ({ node, ...props }) => <p className="mb-1" {...props} />,
  ul: ({ node, ...props }) => <ul className="mb-1 ps-4" style={{ listStyle: 'disc' }} {...props} />,
  ol: ({ node, ...props }) => <ol className="mb-1 ps-4" style={{ listStyle: 'decimal' }} {...props} />,
  li: ({ node, ...props }) => <li className="mb-0" {...props} />,
};

const AiTutorButton = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const endRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    if (endRef.current) {
      endRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  const sendMessage = async () => {
    const content = draft.trim();
    if (!content || isSending) return;

    const next = [...messages, { role: 'user', content }].slice(-MAX_HISTORY);
    setMessages(next);
    setDraft('');
    setErrorMessage('');
    setIsSending(true);

    try {
      const data = await askAiTutor(next);
      setMessages((prev) => [...prev, { role: 'assistant', content: data.reply }].slice(-MAX_HISTORY));
    } catch (err) {
      setErrorMessage(err.message || 'Nepodařilo se získat odpověď.');
    } finally {
      setIsSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const clearChat = () => {
    setMessages([]);
    setErrorMessage('');
  };

  return (
    <>
      {isOpen && (
        <>
          <div
            className="position-fixed top-0 start-0 w-100 h-100"
            style={{ backgroundColor: 'rgba(0,0,0,0.4)', zIndex: 1055 }}
            onClick={() => setIsOpen(false)}
            aria-hidden="true"
          />
          <div
            className="position-fixed bg-white rounded shadow-lg d-flex flex-column"
            style={{
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '90vw',
              maxWidth: '520px',
              height: '85vh',
              maxHeight: '700px',
              zIndex: 1060,
            }}
          >
            <div
              className="d-flex align-items-center justify-content-between px-3 py-2 border-bottom bg-light"
              style={{ borderRadius: '0.375rem 0.375rem 0 0' }}
            >
              <strong>AI tutor — zeptej se na cokoli</strong>
              <div className="d-flex gap-2">
                {messages.length > 0 && (
                  <button className="btn btn-sm btn-outline-secondary" onClick={clearChat} disabled={isSending}>
                    Vyčistit
                  </button>
                )}
                <button className="btn btn-sm btn-outline-secondary" onClick={() => setIsOpen(false)}>
                  Zavřít
                </button>
              </div>
            </div>

            <div className="flex-grow-1 overflow-auto px-3 py-2" style={{ backgroundColor: '#f8f9fa' }}>
              {messages.length === 0 && !isSending && (
                <div className="text-muted small mt-3">
                  <p className="mb-2"><strong>Můžeš se zeptat například:</strong></p>
                  <ul className="ps-3 mb-0">
                    <li>Jaký je rozdíl mezi <em>some</em> a <em>any</em>?</li>
                    <li>Kdy se používá present perfect?</li>
                    <li>Co znamená slovo <em>although</em>?</li>
                    <li>Jak řeknu anglicky „včera jsem byl doma"?</li>
                  </ul>
                </div>
              )}

              {messages.map((msg, idx) => (
                <div key={idx} className={`mb-2 d-flex ${msg.role === 'user' ? 'justify-content-end' : 'justify-content-start'}`}>
                  <div
                    className={`px-3 py-2 rounded ${msg.role === 'user' ? 'bg-primary text-white' : 'bg-white border'}`}
                    style={{ maxWidth: '85%', whiteSpace: msg.role === 'user' ? 'pre-wrap' : 'normal' }}
                  >
                    {msg.role === 'assistant' ? (
                      <ReactMarkdown components={compactMarkdown}>{msg.content}</ReactMarkdown>
                    ) : (
                      msg.content
                    )}
                  </div>
                </div>
              ))}

              {isSending && (
                <div className="d-flex justify-content-start mb-2">
                  <div className="px-3 py-2 rounded bg-white border text-muted small">
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                    Přemýšlím…
                  </div>
                </div>
              )}

              {errorMessage && (
                <div className="alert alert-danger py-2 px-3 small mb-2">{errorMessage}</div>
              )}

              <div ref={endRef} />
            </div>

            <div className="border-top p-2">
              <div className="input-group">
                <textarea
                  ref={inputRef}
                  className="form-control"
                  placeholder="Napiš dotaz (Enter odešle, Shift+Enter nový řádek)…"
                  rows={2}
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={handleKeyDown}
                  disabled={isSending}
                  style={{ resize: 'none' }}
                />
                <button
                  className="btn btn-primary"
                  onClick={sendMessage}
                  disabled={isSending || !draft.trim()}
                >
                  Odeslat
                </button>
              </div>
            </div>
          </div>
        </>
      )}
      {!isOpen && (
        <button
          className="btn btn-primary position-fixed bottom-0 m-4 rounded-circle shadow"
          style={{
            width: '56px',
            height: '56px',
            zIndex: 1040,
            right: 'calc(1.5rem + 56px + 1rem + 56px + 1rem + 56px + 1rem)',
          }}
          onClick={() => setIsOpen(true)}
          aria-label="Otevřít AI tutora"
          title="AI tutor — zeptej se na cokoli o angličtině"
        >
          <svg aria-hidden="true" viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
            <path d="M12 2a7 7 0 0 0-7 7c0 2.38 1.19 4.47 3 5.74V17a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1v-2.26c1.81-1.27 3-3.36 3-5.74a7 7 0 0 0-7-7Zm-2 19a1 1 0 0 0 1 1h2a1 1 0 0 0 1-1v-1h-4v1Z" />
          </svg>
        </button>
      )}
    </>
  );
};

export default AiTutorButton;
